/**
 * Which URLs survive the signed-in user or organization changing in another
 * tab.
 *
 * `onCheckToken$` replaces the current URL with a safe one when it notices
 * that change, and the reason is sound: most of this application's URLs name
 * an account-scoped resource — a project, a service, an invoice — which the
 * new user may not be able to read, so loading it would 403 or paint a blank
 * page.
 *
 * A few URLs name nothing of the sort, and for those the redirect is pure
 * loss. `/authorize-app` carries a client mode, a nonce and a destination;
 * the consent it renders mints a token for whoever is signed in *now*. There
 * is nothing on it to 403, and it cannot be restarted from the dashboard —
 * the request only exists in that URL. Replacing it is how "Continue with
 * your Zerops account" ends up on the dashboard with the hand-over gone.
 *
 * Deliberately a list of exceptions rather than a rule: the redirect is the
 * safe default, and a path earns its way out of it by demonstrably naming no
 * account-scoped resource.
 */

// Case-sensitive, because Angular's router is: `/AUTHORIZE-APP` matches no
// route and falls to `**` anyway, so matching it here would whitelist a URL
// that gets redirected regardless and pin a behaviour the router lacks.
const SESSION_AGNOSTIC_PATHS = [ /^\/authorize-app$/u, /^\/authorize-login$/u ];

export const urlSurvivesSessionChange = (pathname: string): boolean => {
  const normalized = pathname.replace(/\/+$/u, '') || '/';
  return SESSION_AGNOSTIC_PATHS.some((pattern) => pattern.test(normalized));
};
